import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const NavBar = () => {
  const [menuOpen, setMenuOpen] = useState(false);

  const navLinks = [
    { name: 'About Us', path: '/about' },
    { name: 'Find a Study Spot', path: '/search-page' },
    {
      name: 'Find an Empty Classroom',
      path: '/availability',
      isPrimary: true
    },
  ];

  const NavLink = ({ link, isMobile = false }) => {
    const baseStyles = "transition-all duration-300 font-medium touch-manipulation whitespace-nowrap no-underline";
    const mobileStyles = "block px-6 py-3 text-base rounded-xl min-h-[44px]";
    const desktopStyles = "px-5 py-2 rounded-full text-sm";
    
    const getLinkStyles = () => {
      if (link.isPrimary) {
        return `${baseStyles} ${isMobile ? mobileStyles : desktopStyles} 
          bg-white text-black hover:text-black visited:text-black border border-white
          ${!isMobile && 'hover:bg-gray-200'}
          shadow-lg shadow-white/5`;
      }
      return `${baseStyles} ${isMobile ? mobileStyles : desktopStyles} 
        text-gray-300 hover:text-white visited:text-gray-300 ${!isMobile && 'hover:bg-white/5'} border border-transparent`;
    };

    return (
      <Link
        to={link.path}
        onClick={() => setMenuOpen(false)}
        className={getLinkStyles()}
      >
        {link.name}
      </Link>
    );
  };

  return (
    <div className="fixed top-0 left-0 right-0 z-50 flex justify-center p-4">
      <motion.nav 
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className={`
          w-full max-w-5xl mx-auto
          bg-[#0a0a0a]/80 backdrop-blur-xl border border-white/10
          shadow-2xl shadow-black/50
          rounded-2xl px-4 py-3 sm:px-6 sm:py-3
          transition-all duration-300
        `}
      >
        <div className="flex justify-between items-center">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center space-x-3 group p-1 touch-manipulation"
          >
            <img
              src="/WLUMAPS1.png"
              alt="WLUMAPS Logo"
              className="h-8 w-8 group-hover:scale-105 transition-transform duration-300"
            />
            <span className="text-lg font-bold text-white tracking-tight">
              WLU<span className="text-purple-400">MAPS</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-2">
            {navLinks.map((link) => (
              <NavLink key={link.name} link={link} />
            ))}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="lg:hidden p-2 rounded-xl text-gray-400 hover:text-white hover:bg-white/5 transition-all duration-300"
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          >
            {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu Dropdown */}
        <AnimatePresence>
          {menuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0, marginTop: 0 }}
              animate={{ opacity: 1, height: 'auto', marginTop: 16 }}
              exit={{ opacity: 0, height: 0, marginTop: 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="lg:hidden overflow-hidden"
            >
              <div className="space-y-2 pb-2">
                {navLinks.map((link) => (
                  <NavLink key={link.name} link={link} isMobile />
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.nav>
    </div>
  );
};

export default NavBar;